import React, { useState, useEffect } from "react";
import { Button, Typography } from "antd";
import { LeftOutlined, RightOutlined } from "@ant-design/icons";
import { HotelStars } from "../../common";
import { renderNumber } from "@share/utils";
import { ActivitySavedProperty } from "@share/components";
import { IActivity } from "@common-types";

const { Title } = Typography;



interface Image {
    resource: string;
}

interface CarouselImageProps {
    images: Image[];
    activityName?: string;
    isGalleryOpen: boolean;
    name?: string;
    review: number;
    rating: number;
    onOpenGallery: (index: number) => void;
    activity?: IActivity;
}

export const CarouselImage: React.FC<CarouselImageProps> = ({
    images,
    activityName,
    isGalleryOpen,
    name,
    review,
    rating,
    onOpenGallery,
    activity,
}) => {
    const [carouselIndex, setCarouselIndex] = useState(0);

    useEffect(() => {
        if (!images?.length || isGalleryOpen) return;

        const interval = setInterval(() => {
            setCarouselIndex((prev) => (prev + 1) % (images.length || 1));
        }, 5000);

        return () => clearInterval(interval);
    }, [images?.length, isGalleryOpen]);

    if (!images || images.length === 0) return null;

    return (
        <div className="activity-carousel-container">
            <div className="carousel-layout">
                {/* Main Hero Image */}
                <div className="hero-section" onClick={() => onOpenGallery(carouselIndex)}>
                    <img
                        src={images[carouselIndex]?.resource || "https://via.placeholder.com/1200x600"}
                        alt={activityName}
                        className="main-hero-image"
                    />


                    {/* Navigation Arrows */}
                    {images.length > 1 && (
                        <div className="carousel-nav">
                            <Button
                                className="nav-btn prev"
                                icon={<LeftOutlined />}
                                shape="circle"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setCarouselIndex((prev) => (prev - 1 + images.length) % images.length);
                                }}
                            />
                            <Button
                                className="nav-btn next"
                                icon={<RightOutlined />}
                                shape="circle"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setCarouselIndex((prev) => (prev + 1) % images.length);
                                }}
                            />
                        </div>
                    )}

                    <div className="hero-overlay">
                        <div className="title-area">
                            <Title level={2} className="activity-name" ellipsis={{ tooltip: name }}>{name}</Title>
                            <div className="rating-location">
                                {rating > 0 && review > 0 && (
                                    <div className="stars">
                                        <HotelStars stars={rating} />
                                        <span className="review-stats">{renderNumber(rating, 2)} ({review} reviews)</span>
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="action-buttons">
                            {activity && <ActivitySavedProperty activity={activity} />}
                        </div>
                    </div>
                </div>

                {/* Side Vertical Strip */}
                {images.length > 1 && (
                    <div className="side-strip">
                        {images.slice(1, 6).map((img, idx) => {
                            const realIndex = idx + 1;
                            return (
                                <div
                                    key={idx}
                                    className={`strip-item ${carouselIndex === realIndex ? "active" : ""}`}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        if (idx === 4 && images.length > 6) {
                                            onOpenGallery(realIndex);
                                        } else {
                                            setCarouselIndex(realIndex);
                                        }
                                    }}
                                >
                                    <img src={img.resource} alt={`Gallery ${realIndex}`} />
                                    {idx === 4 && images.length > 6 && (
                                        <div className="more-overlay">
                                            <span className="more-text">+{images.length - 6} More</span>
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        </div>
    );
};
